// Порт из ~/tools/src/libs/persist (см. docs/persist.md). Копируем почти как есть, адаптируя под zod 4.

import { z, ZodSafeParseResult, ZodSafeParseSuccess, ZodSchema, ZodType } from 'zod'

export type StorableTypesMap = {
  string: string
  number: number
  date: Date
  boolean: boolean
  object: Record<string, any>

  list_number: number[]
  list_string: string[]
  list_object: Record<string, any>[]
}

export type StorableType = keyof StorableTypesMap

export const schemaFactoriesMap = {
  string: createStringValidation,
  number: createNumberValidation,
  date: createDateValidation,
  boolean: createBooleanValidation,
  object: createObjectValidation,
  list_number: createNumberListValidation,
  list_string: createStringListValidation,
  list_object: createObjectListValidation,
} as const

export type FactoriesMap = typeof schemaFactoriesMap

export type BaseSchemeParameters<TType extends StorableType> = Parameters<FactoriesMap[TType]>[0]

export type BaseValue<TType extends StorableType> = StorableTypesMap[TType]

export type ValidationArgs<TType extends StorableType, TValue> =
  | ({ schema?: ZodSchema<TValue> } & { [Key in keyof BaseSchemeParameters<TType>]?: never })
  | ({ schema?: never } & BaseSchemeParameters<TType>)

// String Validation

export type StringValidation = {
  minLength?: number
  maxLength?: number
}

export function createStringValidation(validation: StringValidation) {
  const { minLength, maxLength } = validation

  let scheme = z.string()

  if (minLength) {
    scheme = scheme.min(minLength)
  }

  if (maxLength) {
    scheme = scheme.max(maxLength)
  }

  return scheme
}

// Number Validation

export type NumberValidation = {
  min?: number
  max?: number
}

export function createNumberValidation(validation: NumberValidation) {
  const { min, max } = validation

  let scheme = z.number()

  if (min) {
    scheme = scheme.min(min)
  }

  if (max) {
    scheme = scheme.max(max)
  }

  return scheme
}

// Date Validation

export type DateValidation = {
  minDate: Date
  maxDate: Date
}

export function createDateValidation(validation: DateValidation) {
  const { minDate, maxDate } = validation

  let scheme = z.coerce.date()

  if (minDate) {
    scheme = scheme.min(minDate)
  }

  if (maxDate) {
    scheme = scheme.max(maxDate)
  }

  return scheme
}

// Boolean Validation

export function createBooleanValidation() {
  return z.boolean()
}

export function createNumberListValidation() {
  return z.array(z.number())
}

export function createStringListValidation() {
  return z.array(z.string())
}

// zod 4: `z.object({}).passthrough()` устарел, актуальный аналог — `z.looseObject({})`
export function createObjectValidation() {
  return z.looseObject({})
}

export function createObjectListValidation() {
  return z.array(createObjectValidation())
}

// Scheme generation

export function getValidationSchemeFromConfig<
  TType extends StorableType,
  TValue extends BaseValue<TType>,
>(type: TType, validationArgs?: ValidationArgs<TType, TValue>): ZodType<TValue> | null {
  if (!!validationArgs && 'schema' in validationArgs && validationArgs.schema) {
    return validationArgs.schema
  }

  const schemaFactory = schemaFactoriesMap[type] as (
    config: ValidationArgs<TType, TValue>,
  ) => ZodType<TValue>

  return schemaFactory(validationArgs ?? {})
}

export function validateByScheme<TValue>(
  value: unknown,
  scheme: ZodSchema | null,
): ZodSafeParseResult<TValue> {
  if (!scheme) {
    return { success: true, data: value } as ZodSafeParseSuccess<TValue>
  }

  return scheme.safeParse(value) as ZodSafeParseResult<TValue>
}
